/**
 * Один тенант-справочник = список + разбор в СОСЕДНЕЙ вкладке (AK-546, ч.2).
 *
 * Снимает перекос Р-4 из продуктовой сессии 05.08: раньше очередь разбора была
 * центральным экраном справочника — админ приходил вести культуры, а попадал в
 * очередь. Теперь «Справочник» — список для ежедневной работы, а разбор порций
 * живёт во вкладке «Интеграции и импорт» (Р-3: статус интеграции ≠ статус
 * справочника, и одно другому не мешает — П-2).
 */
import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { api } from '@/lib/api';
import { useCan } from '@/lib/auth/usePermissions';
import {
  useReviewSummary,
  type ReviewEntityType,
} from '@/lib/reference/catalogReview';
import { CatalogReviewTab } from './catalog-review/CatalogReviewTab';
import { SufficiencyCard } from './catalog-review/SufficiencyCard';

/**
 * Первая компания тенанта — тот же источник, что у AppShell и соседних форм
 * (`CompanyOpTypeDialog`).
 *
 * TODO(AK-546): для многокомпанийного холдинга это молча НЕ та компания —
 * декларация Р-2 запишется в первую. Долг общий с соседями, чинится вместе с
 * ними (нужен явный выбор компании в шапке раздела).
 */
export function useFirstCompanyId(): string | null {
  const { data } = useQuery({
    queryKey: ['companies'],
    queryFn: () => api.get<Array<{ id: string }>>('/api/v1/companies'),
    staleTime: 5 * 60_000,
  });
  return data?.[0]?.id ?? null;
}

export function CatalogSection({
  entityType,
  children,
}: {
  entityType: ReviewEntityType;
  /** Экран-список этого справочника (ежедневная работа). */
  children: ReactNode;
}) {
  const [tab, setTab] = useState('catalog');
  const companyId = useFirstCompanyId();
  // Решение Е: разбор порций — ОТДЕЛЬНОЕ право, а не часть редактирования.
  const canReview = useCan('catalog_review', 'r');
  const canDecide = useCan('catalog_review', 'u');
  // Решение Д: плашка Р-2 — только редактору справочника.
  const canEditCatalog = useCan('company_reference', 'u');

  const summary = useReviewSummary(entityType, canReview);
  const pending = summary.data?.pending_in_batches ?? 0;

  return (
    <div className="flex flex-col gap-4">
      <Tabs value={tab} onValueChange={setTab} className="flex flex-col gap-4">
        <TabsList className="self-start">
          <TabsTrigger value="catalog">Справочник</TabsTrigger>
          {canReview && (
            <TabsTrigger value="review" className="gap-2">
              Интеграции и импорт
              {pending > 0 && <Badge variant="accent-brand">{pending}</Badge>}
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="catalog" className="flex flex-col gap-4">
          {/* Плашка Р-2 принадлежит СПРАВОЧНИКУ, а не интеграциям: на вкладке
              разбора она не при чём (wireframe, сост. 1). */}
          <SufficiencyCard
            entityType={entityType}
            companyId={companyId}
            canEdit={canEditCatalog}
          />
          {/* Баннера с числами разбора здесь больше НЕТ (UAT founder 07.08):
              «Справочник это справочник» — экран показывает результат, а не
              статус настройки. Тихая строка «N названий ждут разбора» живёт
              внутри самого списка, а цифры разбора — на вкладке интеграций. */}
          {children}
        </TabsContent>

        {canReview && (
          <TabsContent value="review">
            <CatalogReviewTab
              entityType={entityType}
              canRead={canReview}
              canDecide={canDecide}
            />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

export default CatalogSection;
